'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useScrollReset } from '@hooks/useScrollReset';
import cn from './style.module.scss';

/* ------------------ TYPES ------------------ */

type Props = {
  children: React.ReactNode;
  t?: any;
  logoImage?: any;
  hideLogo?: boolean;
  headerClassName?: string;
  scrolledClassName?: string;
  dynamicClassName?: string | ((isScrolledUp: boolean) => string);
  fixed?: boolean;
};

/* ------------------ COMPONENT ------------------ */

export default function DynamicScrollHeader({
  children,
  hideLogo = false,
  headerClassName = '',
  scrolledClassName = '',
  dynamicClassName,
  fixed = false,
}: Props) {
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const lastScrollY = useRef(0);

  /* ------------------ SCROLL LOGIC ------------------ */
  useEffect(() => {
    if (fixed) return;

    const handleScroll = () => {
      const currentY = window.scrollY;

      if (currentY > 100) {
        setIsScrolledUp(currentY < lastScrollY.current);
      } else {
        setIsScrolledUp(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fixed]);

  useScrollReset();

  /* ------------------ DYNAMIC CLASS ------------------ */
  const resolvedDynamicClass =
    typeof dynamicClassName === 'function'
      ? dynamicClassName(isScrolledUp)
      : dynamicClassName;

  /* ------------------ CLASSES ------------------ */
  const headerClasses = [
    cn.dynamicHeader,
    isScrolledUp && cn.scrolledFixed,
    isScrolledUp && scrolledClassName,
    headerClassName,
    resolvedDynamicClass,
  ]
    .filter(Boolean)
    .join(' ');

  /* ------------------ RENDER ------------------ */
  return <header className={headerClasses}>{children}</header>;
}
